import { isFuture, isValid } from "date-fns";
import Logger from "@cappelendamm/logger";
import { updateSubscriptionContract } from "../mutations/updateSubscriptionContract.subscriptions.js";
import { getSubscriptionContract } from "../queries/getSubscriptionContract.core.js";
import { buildHoldbackMeta } from "../utils/buildHoldbackMeta.js";
import { resolveHoldbackGiftOrder } from "../utils/buildHoldbackGiftOrder.js";
import { isContractOwner } from "../utils/isContractOwner.js";
import { isOnHoldback } from "../utils/isOnHoldback.js";
import { createHoldbackGiftOrder } from "./createHoldbackGiftOrder.js";
import { getSubscriptionContractDetails } from "./getSubscriptionContractDetails.js";
/**
 * Pauses (holdback) an active book club contract until `renewAt` instead of
 * cancelling it. A new binding period starts today. When `giftSku` is set, a
 * gift order (the gift at 0 kr plus the club's freight) is created after the
 * contract is paused.
 */
export const onHoldback = async ({ contractId, customerIdentifier, renewAt, giftSku }, { client }) => {
    Logger.log(`Holdback for contractId: ${contractId} until renewAt: ${renewAt}`);
    const details = await getSubscriptionContractDetails({ contractId }, { client });
    if (!isContractOwner(details, customerIdentifier)) {
        throw new Error(`Unauthorized: User is not permitted to hold back contractId: ${contractId}`);
    }
    if (!details.isBookClubSubscription) {
        throw new Error(`Cannot hold back non-book-club contractId: ${contractId}`);
    }
    if (!details.isActive || details.contractPendingStatus) {
        throw new Error(`Cannot hold back contractId: ${contractId} because it is not active`);
    }
    if (isOnHoldback({
        holdbackUntil: details.holdbackUntil,
        pausedUntil: details.delivery.kind === "bookClub"
            ? details.delivery.pausedUntil
            : undefined,
    })) {
        throw new Error(`Cannot hold back contractId: ${contractId} because it is already on holdback`);
    }
    const renewAtDate = new Date(renewAt);
    if (!isValid(renewAtDate) || !isFuture(renewAtDate)) {
        throw new Error(`Cannot hold back contractId: ${contractId} because renewAt must be in the future: ${renewAt}`);
    }
    const dispatch = details.dispatches?.find(({ distributionDate }) => distributionDate === renewAt);
    if (!dispatch?.batchCode) {
        throw new Error(`Cannot hold back contractId: ${contractId} because renewAt matches no dispatch: ${renewAt}`);
    }
    const giftOrder = giftSku
        ? resolveHoldbackGiftOrder({
            contractId,
            giftSku,
            giftConfig: details.giftConfig,
            freight: details.freight.recurring[0],
        })
        : undefined;
    const contract = await getSubscriptionContract({ contractId }, { client });
    const updatedId = await updateSubscriptionContract({
        id: contractId,
        status: {
            price: contract.status?.phase?.price,
            currency: contract.status?.phase?.currency,
            renewAt,
            activeUntil: renewAt,
        },
        meta: buildHoldbackMeta(contract.meta, {
            dispatchBatchCode: dispatch.batchCode,
            holdbackUntil: renewAt,
        }),
    }, { client });
    Logger.log(`Holdback done for contractId: ${contractId}. renewAt changed from ${contract.status?.renewAt} to ${renewAt}`);
    if (!giftOrder) {
        return { contractId: updatedId, renewAt, giftOrder: { status: "none" } };
    }
    try {
        const orderId = await createHoldbackGiftOrder({
            contractId,
            customerIdentifier: details.customerIdentifier,
            contractAddresses: contract.addresses,
            gift: giftOrder.gift,
            freight: giftOrder.freight,
        }, { client });
        Logger.log(`Holdback gift order created for contractId: ${contractId}, orderId: ${orderId}`);
        return {
            contractId: updatedId,
            renewAt,
            giftOrder: { status: "created", orderId },
        };
    }
    catch (error) {
        // The contract is already paused, so the pause must not be reported as failed
        Logger.error(`Holdback gift order failed for contractId: ${contractId}`, error);
        return {
            contractId: updatedId,
            renewAt,
            giftOrder: {
                status: "failed",
                message: error instanceof Error ? error.message : String(error),
            },
        };
    }
};
